import React, { useCallback, useMemo } from 'react'
import config from 'constants/config'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import TextLink from 'components/Luxkit/TextLink'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import moment from 'moment'
import PriceDetailsModal from 'components/Common/PriceDetails/PriceDetailsModal'
import TextButton from 'components/Luxkit/Button/TextButton'
import BusinessTravellerOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerOfferCreditsTextLink'
import { pluralizeToString } from 'lib/string/pluralize'
import noop from 'lib/function/noop'
import { AccommodationPackageTilePricing } from '../Accommodation/AccommodationPackageTile'
import AccommodationPackageTilePricingBlocks from '../Accommodation/AccommodationPackageTilePricingBlocks'
import AccommodationPackageTilePricingBlock from '../Accommodation/AccommodationPackageTilePricingBlock'
import { getDisplayTotalPrice, getDisplayTotalPricePerNight } from 'lib/hotels/bedbankUtils'
import BedbankTwinShare from 'components/OfferList/OfferListTiles/Support/BedbankTwinShare'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowValueDiscount from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscount'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import Label from 'components/Luxkit/Label'
import SolidFireIcon from 'components/Luxkit/Icons/solid/SolidFireIcon'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import { OptimizelyExperiments, getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import { useAppSelector } from 'hooks/reduxHooks'

interface Props {
  promotions?: App.BedbankPromotion;
  rate: App.BedbankRate;
  bundleRate?: App.BedbankRate;
  rooms: Array<App.Occupants>;
  offer: App.BedbankOffer | App.BedbankOfferSummary;
  flightOrigin?: App.Airport;
  flightPrice?: number;
  checkIn?: moment.Moment;
  departureTooEarly?: boolean;
  flightsEnabled: boolean;
  onBookNow?: (rate: App.BedbankRate, flights?: boolean) => void;
}

function BedbankPackageTilePricing(props: Props) {
  const {
    rate,
    bundleRate,
    promotions,
    rooms,
    offer,
    checkIn,
    flightOrigin,
    flightPrice,
    departureTooEarly,
    flightsEnabled,
    onBookNow = noop,
  } = props

  const isPerNightPricingTestEnabled = !!useAppSelector((state: App.State) => getOptimizelyExperimentVariation(state, OptimizelyExperiments.pricePerNight))
  const [deals] = useFlightDeals('all', {
    originAirportCode: flightOrigin?.code,
    destinationAirportCode: offer.flights?.airportCode,
  })
  const offerPageDispatch = useOfferPageDispatch()
  const hasFlights = !!flightPrice
  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterTypes })
  }, [offerPageDispatch])

  const {
    propertyFees = 0,
    taxesAndFees,
  } = rate.totals
  const bundlePropertyFees = bundleRate?.totals.propertyFees || propertyFees
  const bundleTaxesAndFees = bundleRate?.totals.taxesAndFees || taxesAndFees

  const hotelPrice = rate.totals.inclusive
  const bundlePrice = bundleRate?.totals.inclusive || hotelPrice
  const showFlightsDealTag = flightOrigin?.code && offer.flights?.airportCode && flightsEnabled && deals.length > 0

  const departureTooFarInFuture = useMemo(() => {
    if (checkIn) {
      return checkIn.diff(moment.utc(), 'days') > 330
    }
    return false
  }, [checkIn])

  const hasDates = !!checkIn

  const totalBasePrices = getDisplayTotalPrice({
    hotelPrice: hotelPrice + propertyFees,
    hotelValue: rate.value,
    bundlePrice: bundlePrice + bundlePropertyFees,
    bundleValue: bundleRate?.value,
    checkIn,
    flightPrice,
    rooms,
    displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
  })
  const totalPrices = isPerNightPricingTestEnabled && !offer.displayPricingAsPerPerson ? getDisplayTotalPricePerNight(totalBasePrices, rate.nights) : totalBasePrices
  const saleUnit = offer.displayPricingAsPerPerson ? 'person' : isPerNightPricingTestEnabled ? 'night' : 'total'
  const isPromoRate = rate.id === promotions?.id

  return <AccommodationPackageTilePricing>
    {rate.discount > config.displayDiscountThreshold.bedbank && <PriceRowValueDiscount
      type="estimate"
      discountPercentage={Math.round(rate.discount * 100)}
    />}
    <AccommodationPackageTilePricingBlocks>
      <AccommodationPackageTilePricingBlock
        data-testid="pricing"
        title={flightsEnabled ? 'Hotel' : undefined}
        callToAction={<TextButton
          kind="primary"
          data-testid="book-button"
          onClick={() => onBookNow(rate)}
        >
          {hasDates ? 'Book now' : 'Check availability'}
        </TextButton>}
      >
        <PriceRowPriceCaption>
          {pluralizeToString('night', rate.nights)}{hasDates ? ' from' : ''}
        </PriceRowPriceCaption>
        <PriceRowPrice
          size="M"
          price={totalPrices.totals.price}
          saleUnit={saleUnit}
        />
        {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
        {!isPromoRate && <PriceRowTaxesAndFees />}
        {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
          type="exact"
          offer={offer}
          totalCost={totalPrices.original.price}
          numberOfNights={rate.nights}
        />}
        {rate.discount > 0 && <PriceRowValueDiscountWithCaption
          size="M"
          value={totalPrices.totals.value}
        />}
        {!isPromoRate && <PriceDetailsModal
          trigger="price-row"
          triggerSize="M"
          offerPage
          offer={offer}
          rooms={rooms}
          propertyFees={propertyFees}
          taxesAndFees={taxesAndFees}
          hotelPrice={hotelPrice}
          duration={rate.nights}
          cta={
            <TextButton kind="primary" fit="flex">
              {hasDates ? 'Book now' : 'Check availability'}
            </TextButton>
          }
          onCtaAfterClose={() => onBookNow(rate)}
        />}
      </AccommodationPackageTilePricingBlock>
      {flightsEnabled && <AccommodationPackageTilePricingBlock
        data-testid="flights-pricing"
        title="Hotel + flights"
        callToAction={<TextButton
          kind="primary"
          data-testid="book-button"
          onClick={() => onBookNow(bundleRate, true)}
        >
          {hasDates ? 'Book now' : 'Check availability'}
        </TextButton>}
      >
        {!hasFlights && flightOrigin && flightOrigin.code === offer.flights?.airportCode && (
          <div>
            <PriceRowCaption>
              No flights available from {flightOrigin.name}. Please select another airport.
            </PriceRowCaption>
            <TextLink
              onClick={onToggleFilter}
              data-filter="airport"
            >
              Change departure airport
            </TextLink>
          </div>
        )}
        {!hasFlights && flightOrigin?.code !== offer.flights?.airportCode && !departureTooFarInFuture && !departureTooEarly && (
          <PriceRowCaption>
            Add flights to the hotel package. Select from all major airlines at the next step.
          </PriceRowCaption>
        )}
        {!hasFlights && departureTooFarInFuture && (
          <PriceRowCaption>
            Flights can only be booked up to 11 months in advance.
          </PriceRowCaption>
        )}
        {flightOrigin?.code !== offer.flights?.airportCode && departureTooEarly && (
          <div>
            <PriceRowCaption>
              Flights cannot be booked online when check-in is within 24 hours
            </PriceRowCaption>
            <TextLink
              onClick={onToggleFilter}
              data-filter="datepicker"
            >
              Change departure date
            </TextLink>
          </div>
        )}
        {hasFlights && !departureTooEarly && <div>
          <PriceRowPriceCaption>
            {pluralizeToString('night', rate.nights)}{hasDates ? ' from' : ''}
          </PriceRowPriceCaption>
          <PriceRowPrice
            size="M"
            price={totalPrices.totals.flightBundlePrice}
            saleUnit={offer.displayPricingAsPerPerson ? 'person' : isPerNightPricingTestEnabled ? 'night*' : 'total*'}
          />
          {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
          <PriceRowTaxesAndFees />
          {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
            type="exact"
            offer={offer}
            totalCost={totalPrices.original.flightBundlePrice}
            numberOfNights={rate.nights}
          />}
          {rate.discount > 0 && !isPromoRate && <PriceRowValueDiscountWithCaption
            size="M"
            value={totalPrices.totals.flightBundleValue}
          />}
          {showFlightsDealTag && <Label variant="critical" kind="plain" startIcon={<SolidFireIcon />}>Flight deal available</Label>}
          {!isPromoRate && <PriceDetailsModal
            trigger="price-row"
            triggerSize="M"
            offerPage
            offer={offer}
            rooms={rooms}
            propertyFees={bundlePropertyFees}
            taxesAndFees={bundleTaxesAndFees}
            hotelPrice={bundlePrice}
            flightPrice={flightPrice}
            duration={rate.nights}
            cta={<TextButton kind="primary" fit="flex">
              {hasDates ? 'Book now' : 'Check availability'}
            </TextButton>}
            onCtaAfterClose={() => onBookNow(bundleRate, true)}
          />}
        </div>}
        <AtolProtectedTooltip />
      </AccommodationPackageTilePricingBlock>}
    </AccommodationPackageTilePricingBlocks>
  </AccommodationPackageTilePricing>
}

export default BedbankPackageTilePricing
